import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactNode } from 'react';
import { useController, type FieldValues } from 'react-hook-form';
import { Form, Radio, type RadioProps } from 'semantic-ui-react';

import { FieldError } from './field-error';
import type { FieldProps } from './field.types';
import s from './field-radio-group.scss';

export interface RadioGroupOption {
  label: ReactNode;
  value: string | number;
}

export type FieldRadioGroupProps<T extends FieldValues = FieldValues> =
  FieldProps<T, RadioProps, 'checked' | 'value' | 'label'> & {
    /**
     * Controls inline layout:
     * - `'field'` — label and options sit on the same line
     * - `'options'` — options are laid out horizontally
     * - `true` — both field and options are inline
     */
    inline?: 'field' | 'options' | true;
    options: RadioGroupOption[];
  };

/**
 * Radio group field wired to React Hook Form via `useController`.
 *
 * Renders a SUI `Form.Field` with `role="radiogroup"` and a `<label>` for
 * the group name. Maps the `options` array to SUI `Radio` components. Error
 * display and ARIA wiring are handled automatically.
 *
 * For advanced cases requiring custom layouts or per-option descriptions, use
 * multiple `Field.Radio` components instead.
 *
 * All additional props are forwarded to each underlying SUI `Radio`.
 */
export const FieldRadioGroup = <T extends FieldValues>({
  className,
  control,
  id,
  inline,
  label,
  name,
  options,
  required,
  width,
  ...rest
}: FieldRadioGroupProps<T>) => {
  useStyles(s);

  const { field, fieldState } = useController({ control, name });
  const groupId = id ?? name;
  const errorId = `${groupId}-error`;
  const labelId = `${groupId}-label`;
  const hasError = !!fieldState.error;
  const isInlineField = inline === 'field' || inline === true;
  const isInlineOptions = inline === 'options' || inline === true;

  return (
    <Form.Field
      className={classNames(s.root, className, {
        [s.inlineField]: isInlineField,
        [s.inlineOptions]: isInlineOptions,
      })}
      aria-errormessage={hasError ? errorId : undefined}
      aria-invalid={hasError || undefined}
      aria-labelledby={labelId}
      error={hasError}
      id={groupId}
      required={required}
      role="radiogroup"
      width={width}
    >
      {label && (
        // eslint-disable-next-line jsx-a11y/label-has-for -- this is the group label so no single input to associate with `htmlFor`
        <label id={labelId} className={s.label}>
          {label}
        </label>
      )}

      {options.map((option, index) => {
        const key = `${field.name}_${option.value}`;
        return (
          <Radio
            {...rest}
            ref={index === 0 ? field.ref : undefined}
            className={s.option}
            checked={field.value === option.value}
            id={key}
            key={key}
            label={option.label}
            name={field.name}
            onBlur={field.onBlur}
            onChange={(_, { value: radioValue }) => field.onChange(radioValue)}
            value={option.value}
          />
        );
      })}

      <FieldError
        className={s.error}
        id={errorId}
        message={fieldState.error?.message}
      />
    </Form.Field>
  );
};
